import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Search, Edit2, X, Download, Upload, Plus, ChevronLeft, Home } from 'lucide-react';
import * as XLSX from 'xlsx';
import LocationService from '../../Api/LocationService';
import { toast } from 'react-toastify';
import '../../Style/Location.css';
import permissionService from '../../Permission/Permission';
import { useAppHistory } from '../../Api/ServiceProvider';
import Breadcrumbs from '../Breadcrum/BreadcrumbHistory';
import UserService from '../../Api/UserService';
import locationExcel from '../../Assets/templates/location_template.xlsx';

// ─── LocationPopup (unchanged, kept outside to prevent re-renders) ────────────
const LocationPopup = React.memo(({
    showPopup,
    popupMode,
    formData,
    formErrors,
    loading,
    uploading,
    selectedFile,
    uploadError,
    canAdd,
    onClose,
    onSubmit,
    onFormChange,
    onFileChange,
    onResetFileUpload,
    onDownloadTemplate
}) => {
    const fileInputRef = useRef(null);
    const [activeTab, setActiveTab] = useState('manual');

    if (!showPopup) return null;

    return (
        <div className="location-popup-overlay" onClick={onClose}>
            <div className="location-popup-container" onClick={(e) => e.stopPropagation()}>
                <div className="location-popup-header">
                    <h3>{popupMode === 'add' ? 'Add Location' : 'Edit Location'}</h3>
                    <button
                        className="location-popup-close"
                        onClick={onClose}
                        disabled={loading || uploading}
                    >
                        <X size={20} />
                    </button>
                </div>

                {popupMode === 'add' && (
                    <div className="location-popup-tabs">
                        <button
                            className={`location-tab ${activeTab === 'manual' ? 'active' : ''}`}
                            onClick={() => setActiveTab('manual')}
                        >
                            Manual Entry
                        </button>
                        <button
                            className={`location-tab ${activeTab === 'file' ? 'active' : ''}`}
                            onClick={() => setActiveTab('file')}
                        >
                            File Upload
                        </button>
                    </div>
                )}

                <div className="location-popup-content">
                    {activeTab === 'manual' || popupMode === 'edit' ? (
                        <div className="location-manual-form">
                            <div className="location-form-group">
                                <label>Part No: *</label>
                                <input
                                    type="text"
                                    value={formData.partno}
                                    onChange={(e) => onFormChange('partno', e.target.value)}
                                    placeholder="Enter Part Number"
                                    disabled={loading || uploading}
                                    className={formErrors.partno ? 'error' : ''}
                                />
                                {formErrors.partno && <span className="error-message">{formErrors.partno}</span>}
                            </div>

                            <div className="location-form-group">
                                <label>Description: </label>
                                <input
                                    type="text"
                                    value={formData.description}
                                    onChange={(e) => onFormChange('description', e.target.value)}
                                    placeholder="Enter Description"
                                    disabled={loading || uploading}
                                    className={formErrors.description ? 'error' : ''}
                                />
                                {formErrors.description && <span className="error-message">{formErrors.description}</span>}
                            </div>

                            <div className="location-form-group">
                                <label>Location: </label>
                                <input
                                    type="text"
                                    value={formData.location}
                                    onChange={(e) => onFormChange('location', e.target.value)}
                                    placeholder="Enter Location (e.g., Warehouse A, Rack 5)"
                                    disabled={loading || uploading}
                                    className={formErrors.location ? 'error' : ''}
                                />
                                {formErrors.location && <span className="error-message">{formErrors.location}</span>}
                            </div>

                            <div className="location-form-group">
                                <label>Company:</label>
                                <input
                                    type="text"
                                    value={formData.company || ''}
                                    onChange={(e) => onFormChange('company', e.target.value)}
                                    placeholder="Enter Company"
                                    disabled={loading || uploading}
                                    className={formErrors.company ? 'error' : ''}
                                />
                                {formErrors.company && <span className="error-message">{formErrors.company}</span>}
                            </div>

                            <div className="location-form-group">
                                <label>Warehouse:</label>
                                <input
                                    type="text"
                                    value={formData.warehouse || ''}
                                    onChange={(e) => onFormChange('warehouse', e.target.value)}
                                    placeholder="Enter Warehouse"
                                    disabled={loading || uploading}
                                    className={formErrors.warehouse ? 'error' : ''}
                                />
                                {formErrors.warehouse && <span className="error-message">{formErrors.warehouse}</span>}
                            </div>

                            <button
                                className="location-submit-btn"
                                onClick={onSubmit}
                                disabled={loading || uploading || formErrors.partno}
                            >
                                {loading ? 'Processing...' : (popupMode === 'add' ? 'ADD LOCATION' : 'UPDATE LOCATION')}
                            </button>
                        </div>
                    ) : (
                        <div className="location-upload-form">
                            <div className="location-upload-section">
                                <div className="location-upload-icon" style={{ opacity: uploading ? 0.6 : 1 }}>
                                    <Upload size={48} />
                                </div>
                                <h4>Upload Excel File</h4>
                                <p>Select an Excel file containing LOCATION data</p>

                                {selectedFile && (
                                    <div style={{
                                        padding: '10px',
                                        background: uploadError ? '#fff3e0' : '#f0f8ff',
                                        border: `1px solid ${uploadError ? '#ff9800' : '#2196f3'}`,
                                        borderRadius: '6px',
                                        marginBottom: '15px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                                            <span style={{ fontSize: '14px', color: uploadError ? '#e65100' : '#1976d2' }}>
                                                📄 {selectedFile.name}
                                            </span>
                                            {uploading && (
                                                <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px' }}>
                                                    Uploading...
                                                </span>
                                            )}
                                        </div>
                                        {!uploading && (
                                            <button
                                                onClick={onResetFileUpload}
                                                style={{
                                                    background: 'none', border: 'none', color: '#f44336',
                                                    cursor: 'pointer', fontSize: '20px', padding: '0',
                                                    width: '24px', height: '24px', display: 'flex',
                                                    alignItems: 'center', justifyContent: 'center', fontWeight: 'bold'
                                                }}
                                                title="Remove file"
                                            >×</button>
                                        )}
                                    </div>
                                )}

                                {!selectedFile && (
                                    <>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            id="excel-upload"
                                            accept=".xlsx,.xls"
                                            onChange={onFileChange}
                                            style={{ display: 'none' }}
                                            disabled={loading || uploading}
                                        />
                                        <label
                                            htmlFor="excel-upload"
                                            className={`location-choose-file-btn ${(loading || uploading) ? 'disabled' : ''}`}
                                            style={{
                                                opacity: (loading || uploading) ? 0.6 : 1,
                                                cursor: (loading || uploading) ? 'not-allowed' : 'pointer'
                                            }}
                                        >
                                            {uploading ? 'Uploading...' : 'Choose file...'}
                                        </label>
                                    </>
                                )}

                                <div className="location-file-instructions">
                                    <h5>File Format Instructions:</h5>
                                    <ul>
                                        <li>Excel file (.xlsx or .xls format)</li>
                                        <li>First row should contain headers</li>
                                        <li><strong>Required columns:</strong> Part No, Description, Location</li>
                                        <li><strong>Optional columns:</strong> Company, Warehouse</li>
                                        <li>Other columns will be ignored</li>
                                    </ul>
                                </div>

                                <div className="location-template-section" style={{ opacity: (loading || uploading) ? 0.6 : 1 }}>
                                    <span className="template-text">Need a template?</span>
                                    <a
                                        href={locationExcel}
                                        download="location_template.xlsx"
                                        className="location-download-template-btn no-link-style"
                                        style={{
                                            opacity: (loading || uploading) ? 0.6 : 1,
                                            cursor: (loading || uploading) ? 'not-allowed' : 'pointer',
                                            pointerEvents: (loading || uploading) ? 'none' : 'auto'
                                        }}
                                    >
                                        Download Template
                                    </a>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

// ─── LocationView ─────────────────────────────────────────────────────────────
const LocationView = () => {
    const [locations, setLocations] = useState([]);
    const [userList, setUserList] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showPopup, setShowPopup] = useState(false);
    const [popupMode, setPopupMode] = useState('add');
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [loading, setLoading] = useState(false);

    const userDataRef = useRef(null);

    const [canView, setCanView] = useState(false);
    const [canAdd, setCanAdd] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canDelete, setCanDelete] = useState(false);

    const emptyForm = { partno: '', description: '', location: '', company: '', warehouse: '' };
    const formDataRef = useRef({ ...emptyForm });
    const [formData, setFormData] = useState({ ...emptyForm });
    const [formErrors, setFormErrors] = useState({ ...emptyForm });

    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadError, setUploadError] = useState(null);
    const [uploading, setUploading] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(25);
    const [totalCount, setTotalCount] = useState(0);

    // ─── Refs to prevent duplicate calls ─────────────────────────────────────
    const isInitializedRef = useRef(false);
    const isFetchingRef = useRef(false);
    const isFirstPageEffect = useRef(true); // skip initial mount for page/size effect
    const searchTimeoutRef = useRef(null);
    const isSearchModeRef = useRef(false);

    const routes = useAppHistory();

    // ─── SINGLE INITIALIZATION EFFECT ────────────────────────────────────────
    useEffect(() => {
        if (isInitializedRef.current) return;
        isInitializedRef.current = true;

        const user = JSON.parse(localStorage.getItem("user") || "null");
        userDataRef.current = user;

        if (!user) return;

        const hasView = permissionService.hasPermission('location', 'view');
        setCanView(hasView);
        setCanAdd(permissionService.hasPermission('location', 'add'));
        setCanEdit(permissionService.hasPermission('location', 'edit'));
        setCanDelete(permissionService.hasPermission('location', 'delete'));

        // Fetch users (independent, non-blocking)
        fetchUsers();

        if (!hasView) {
            toast.error("You don't have permission to access Location module", {
                autoClose: 5000,
                position: "top-center"
            });
            return;
        }

        // Single locations fetch on init — pass params directly
        fetchLocationsWithParams(1, itemsPerPage, '', false);
    }, []);

    // ─── PAGE / PAGE-SIZE CHANGES (skip initial mount) ───────────────────────
    useEffect(() => {
        if (isFirstPageEffect.current) {
            isFirstPageEffect.current = false;
            return;
        }
        if (!userDataRef.current || !canView) return;
        fetchLocationsWithParams(currentPage, itemsPerPage, searchQuery, isSearchModeRef.current);
    }, [currentPage, itemsPerPage]);

    // ─── SEARCH WITH DEBOUNCE ─────────────────────────────────────────────────
    useEffect(() => {
        if (!userDataRef.current || !canView) return;

        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

        // Clear search → reload immediately
        if (searchQuery === '') {
            isSearchModeRef.current = false;
            setCurrentPage(1);
            fetchLocationsWithParams(1, itemsPerPage, '', false);
            return;
        }

        // Only fire when >= 3 chars
        if (searchQuery.length < 3) return;

        searchTimeoutRef.current = setTimeout(() => {
            isSearchModeRef.current = true;
            setCurrentPage(1);
            fetchLocationsWithParams(1, itemsPerPage, searchQuery, true);
        }, 500);

        return () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        };
    }, [searchQuery]);

    // ─── CORE FETCH (accepts params to avoid stale closures) ─────────────────
    const fetchLocationsWithParams = async (page, perPage, search, isSearchMode) => {
        if (!userDataRef.current || isFetchingRef.current) return;

        isFetchingRef.current = true;
        setLoading(true);
        try {
            const response = await LocationService.getAll(page, perPage, search);
            if (response.success) {
                setLocations(response.data || []);
                setTotalCount(response.count || 0);
            } else {
                setLocations([]);
                setTotalCount(0);
                if (!isSearchMode) toast.error(response.error || 'Failed to fetch locations');
            }
        } catch (err) {
            console.error(err);
            setLocations([]);
            setTotalCount(0);
            if (!isSearchMode) toast.error(err?.error || 'Unable to fetch locations');
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    };

    // ─── fetchLocations (uses current state — for post-mutation refreshes) ────
    const fetchLocations = useCallback(() => {
        fetchLocationsWithParams(currentPage, itemsPerPage, searchQuery, isSearchModeRef.current);
    }, [currentPage, itemsPerPage, searchQuery]);

    const fetchUsers = async () => {
        try {
            const data = await UserService.getAll(null, null, '');
            setUserList(data.users);
        } catch {
            setUserList([]);
        }
    };

    // ─── PAGINATION ───────────────────────────────────────────────────────────
    const pageCount = useMemo(() => Math.max(1, Math.ceil(totalCount / itemsPerPage)), [totalCount, itemsPerPage]);
    const handleFirstPage = () => setCurrentPage(1);
    const handlePreviousPage = () => setCurrentPage(prev => Math.max(1, prev - 1));
    const handleNextPage = () => setCurrentPage(prev => Math.min(pageCount, prev + 1));
    const handleLastPage = () => setCurrentPage(pageCount);
    const handlePageSizeChange = (e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); };

    // ─── VALIDATION ───────────────────────────────────────────────────────────
    const validateField = useCallback((field, value) => {
        if (field === 'partno') {
            if (!value.trim()) return 'Part Number is required';
            if (value.length > 50) return 'Part Number must be less than 50 characters';
        }
        return '';
    }, []);

    const validateForm = useCallback(() => {
        const errors = { partno: validateField('partno', formDataRef.current.partno) };
        setFormErrors(prev => ({ ...prev, ...errors }));
        return !errors.partno;
    }, [validateField]);

    // ─── FORM HANDLERS ────────────────────────────────────────────────────────
    const handleFormChange = useCallback((field, value) => {
        formDataRef.current = { ...formDataRef.current, [field]: value };
        setFormData(prev => ({ ...prev, [field]: value }));
        setFormErrors(prev => ({ ...prev, [field]: validateField(field, value) }));
    }, [validateField]);

    const resetFormState = useCallback(() => {
        formDataRef.current = { ...emptyForm };
        setFormData({ ...emptyForm });
        setFormErrors({ ...emptyForm });
    }, []);

    // ─── ADD / EDIT ───────────────────────────────────────────────────────────
    const handleAddLocation = useCallback(() => {
        if (!canAdd) { toast.error("You don't have permission to add locations"); return; }
        setPopupMode('add');
        resetFormState();
        setSelectedLocation(null);
        setSelectedFile(null);
        setUploadError(null);
        setShowPopup(true);
    }, [canAdd, resetFormState]);

    const handleEditLocation = useCallback((location) => {
        if (!canEdit) { toast.error("You don't have permission to edit locations"); return; }
        setPopupMode('edit');
        const data = {
            partno: location.partno || '',
            description: location.description || '',
            location: location.location || '',
            company: location.company || '',
            warehouse: location.warehouse || ''
        };
        formDataRef.current = data;
        setFormData(data);
        setFormErrors({ ...emptyForm });
        setSelectedLocation(location);
        setShowPopup(true);
    }, [canEdit]);

    // ─── SUBMIT ───────────────────────────────────────────────────────────────
    const handleSubmit = useCallback(async () => {
        if (!validateForm()) return;
        if (popupMode === 'add' && !canAdd) { toast.error("You don't have permission to add locations"); return; }
        if (popupMode === 'edit' && !canEdit) { toast.error("You don't have permission to edit locations"); return; }

        setLoading(true);
        try {
            if (popupMode === 'add') {
                const response = await LocationService.createSingle(
                    { ...formDataRef.current },
                    userDataRef.current.accessToken
                );
                if (response.message) {
                    toast.success('Location added successfully!');
                    fetchLocations();
                    handleClosePopup();
                } else {
                    toast.error(response.error || 'Failed to add location');
                }
            } else {
                const response = await LocationService.update(
                    selectedLocation._id,
                    { ...formDataRef.current },
                    userDataRef.current.accessToken
                );
                if (response.success) {
                    toast.success('Location updated successfully!');
                    fetchLocations();
                    handleClosePopup();
                } else {
                    toast.error(response.error || 'Failed to update location');
                }
            }
        } catch {
            toast.error(`Failed to ${popupMode === 'add' ? 'add' : 'update'} location`);
        } finally {
            setLoading(false);
        }
    }, [popupMode, canAdd, canEdit, validateForm, fetchLocations, selectedLocation]);

    // ─── FILE UPLOAD ──────────────────────────────────────────────────────────
    const processExcelFile = useCallback(async (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (evt) => {
                try {
                    const wb = XLSX.read(evt.target.result, { type: 'binary' });
                    const ws = wb.Sheets[wb.SheetNames[0]];
                    const data = XLSX.utils.sheet_to_json(ws);

                    const locationsToImport = data.map((row) => {
                        const partno = row['Item Selection'] || row['Part No'] || row['PartNo'];
                        if (!partno || partno.toString().trim() === '') return null;
                        return {
                            partno: partno.toString().trim(),
                            description: row['Item Description'] || row['Description'] || row['description'] || '',
                            location: row['Location'] || row['location'] || '',
                            company: row['Company'] || row['company'] || '',
                            warehouse: row['Wh'] || row['warehouse'] || '',
                        };
                    }).filter(Boolean);

                    if (locationsToImport.length === 0) {
                        reject(new Error('Invalid Location Excel format. Please ensure Part No column is present and not empty.'));
                        return;
                    }
                    resolve(locationsToImport);
                } catch {
                    reject(new Error('Error reading Excel file. Please check the format.'));
                }
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsBinaryString(file);
        });
    }, []);

    const handleFileChange = useCallback(async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!canAdd) { toast.error("You don't have permission to add locations"); return; }

        setSelectedFile(file);
        setUploadError(null);
        setUploading(true);

        try {
            const locationsToImport = await processExcelFile(file);
            const response = await LocationService.create(locationsToImport, userDataRef.current.accessToken);
            if (response.message) {
                toast.success(`${locationsToImport.length} locations imported successfully!`);
                fetchLocations();
                handleClosePopup();
            } else {
                throw new Error(response.error || 'Failed to import locations');
            }
        } catch (error) {
            setUploadError(error.message);
            toast.error(error.message);
        } finally {
            setUploading(false);
        }
    }, [canAdd, processExcelFile, fetchLocations]);

    const resetFileUpload = useCallback(() => {
        setSelectedFile(null);
        setUploadError(null);
        setUploading(false);
    }, []);

    const handleClosePopup = useCallback(() => {
        setShowPopup(false);
        resetFormState();
        setSelectedLocation(null);
        resetFileUpload();
    }, [resetFormState, resetFileUpload]);

    const downloadTemplate = useCallback(() => {
        if (!canView) { toast.error("You don't have permission to access this feature"); return; }
        const templateData = [
            { 'Part No': 'EXAMPLE001', 'Description': 'Sample Part Description', 'Location': 'Warehouse A, Rack 5, Bin 2', 'Company': 'Example Company', 'Wh': 'Warehouse A' },
            { 'Part No': 'EXAMPLE002', 'Description': 'Another Sample Part', 'Location': 'Store Room B, Shelf 3', 'Company': 'Example Company', 'Wh': 'Warehouse B' }
        ];
        const ws = XLSX.utils.json_to_sheet(templateData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Location Template');
        ws['!cols'] = [{ wch: 15 }, { wch: 30 }, { wch: 25 }, { wch: 20 }, { wch: 15 }];
        XLSX.writeFile(wb, 'Location_Import_Template.xlsx');
    }, [canView]);

    // ─── RENDER ───────────────────────────────────────────────────────────────
    return (
        <div className="location-container">
            <Breadcrumbs />

            <div className="location-sticky-header">
                <div className="location-header">
                    <div className="location-search-box">
                        <Search size={16} />
                        <input
                            type="text"
                            placeholder="Search by Part No, Description, or Location..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            disabled={loading}
                        />
                        {searchQuery && (
                            <X
                                size={16}
                                style={{ cursor: 'pointer', marginLeft: '8px', color: '#666' }}
                                onClick={() => setSearchQuery('')}
                            />
                        )}
                    </div>
                    {canAdd && (
                        <button
                            style={{ marginLeft: '10px' }}
                            className="location-add-btn"
                            onClick={handleAddLocation}
                            disabled={loading}
                        >
                            Add Location
                        </button>
                    )}
                </div>
            </div>

            <div className="location-scrollable-container">
                <div className="location-table-container">
                    <div className="location-table-wrapper">
                        <table className="location-table">
                            <thead>
                                <tr>
                                    <th>S NO</th>
                                    <th>PART NO</th>
                                    <th>DESCRIPTION</th>
                                    <th>LOCATION</th>
                                    <th>COMPANY</th>
                                    <th>WAREHOUSE</th>
                                    <th>DATE CREATED</th>
                                    <th>MODIFIED DATE</th>
                                    <th>EMPLOYEE</th>
                                    {canEdit && <th>ACTION</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {locations.map((location, idx) => (
                                    <tr key={location._id || location.id}>
                                        <td>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                                        <td className="location-part-no">{location.partno}</td>
                                        <td className="location-description">{location.description || 'N/A'}</td>
                                        <td className="location-address">{location.location || 'N/A'}</td>
                                        <td>{location.company || 'N/A'}</td>
                                        <td>{location.warehouse || 'N/A'}</td>
                                        <td className="location-address">
                                            {location.cd
                                                ? new Date(location.cd).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                                                : 'N/A'}
                                        </td>
                                        <td className="location-address">
                                            {location.md
                                                ? new Date(location.md).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                                                : 'N/A'}
                                        </td>
                                        <td className="location-employee">
                                            {`${userList.find((e) => e.uid === location.employee_id)?.name || 'N/A'} - (${location.employee_id?.slice(-6) || ''})`}
                                        </td>
                                        {canEdit && (
                                            <td>
                                                <button
                                                    className="location-edit-btn"
                                                    onClick={() => handleEditLocation(location)}
                                                    disabled={loading}
                                                    title="Edit location"
                                                >
                                                    <Edit2 size={16} />
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="location-pagination">
                    <div className="location-rows-per-page">
                        <span>Rows per page:</span>
                        <select value={itemsPerPage} onChange={handlePageSizeChange} disabled={loading}>
                            {[10, 25, 50, 100].map(size => <option key={size} value={size}>{size}</option>)}
                        </select>
                    </div>

                    <span className="location-page-info">
                        Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalCount)} to{' '}
                        {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} entries
                    </span>

                    <div className="location-pagination-controls">
                        <button onClick={handleFirstPage} disabled={currentPage === 1 || loading}>⟪</button>
                        <button onClick={handlePreviousPage} disabled={currentPage === 1 || loading}>⟨</button>
                        {totalCount > 0
                            ? <span className="location-page-numbers">Page {currentPage} of {pageCount}</span>
                            : <span className="location-page-numbers">Page 0 of 0</span>
                        }
                        <button onClick={handleNextPage} disabled={currentPage >= pageCount || loading}>⟩</button>
                        <button onClick={handleLastPage} disabled={currentPage >= pageCount || loading}>⟫</button>
                    </div>
                </div>
            </div>

            <LocationPopup
                showPopup={showPopup}
                popupMode={popupMode}
                formData={formData}
                formErrors={formErrors}
                loading={loading}
                uploading={uploading}
                selectedFile={selectedFile}
                uploadError={uploadError}
                canAdd={canAdd}
                onClose={handleClosePopup}
                onSubmit={handleSubmit}
                onFormChange={handleFormChange}
                onFileChange={handleFileChange}
                onResetFileUpload={resetFileUpload}
                onDownloadTemplate={downloadTemplate}
            />
        </div>
    );
};

export default LocationView;